import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import postureInstructionsService from '../api/postureInstructionsService';
import type {
    PostureInstruction,
    CreatePostureInstructionDto,
    UpdatePostureInstructionDto,
} from '../types/types';

export const usePostureInstructions = () => {
    return useQuery<PostureInstruction[]>({
        queryKey: ['postureInstructions'],
        queryFn: () => postureInstructionsService.getAll(),
        refetchOnWindowFocus: false,
    });
};

export const usePostureInstruction = (id: number | undefined) => {
    return useQuery<PostureInstruction>({
        queryKey: ['postureInstruction', id],
        queryFn: () => postureInstructionsService.getById(id!),
        enabled: id != null,
        refetchOnWindowFocus: false,
    });
};

export const useCreatePostureInstruction = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (dto: CreatePostureInstructionDto) => postureInstructionsService.create(dto),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['postureInstructions'] }),
    });
};

export const useUpdatePostureInstruction = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dto }: { id: number; dto: UpdatePostureInstructionDto }) =>
            postureInstructionsService.update(id, dto),
        onSuccess: (_, { id }) => {
            qc.invalidateQueries({ queryKey: ['postureInstructions'] });
            qc.invalidateQueries({ queryKey: ['postureInstruction', id] });
        },
    });
};

export const useDeletePostureInstruction = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => postureInstructionsService.remove(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['postureInstructions'] }),
    });
};
